import { hasRole, RoleTypes } from '../../../core/components/Permission';
import {
  CLAIM_DECISION_STATUSES,
  getValidClaimStatusTransitions,
  isClaimDecisionLocked,
  ReclamationStatus,
} from '../models';

export const getCompletionInfo = (reclamation) => {
  const hasCustomerInfo = Boolean(
    String(reclamation.customerName || '').trim() &&
      String(reclamation.customerEmail || '').trim() &&
      String(reclamation.customerPhone || '').trim()
  );
  const hasVehicleInfo = Boolean(
    reclamation.vehicleDetails &&
      (String(reclamation.vehicleDetails.plate || '').trim() ||
        String(reclamation.vehicleDetails.make || '').trim() ||
        String(reclamation.vehicleDetails.model || '').trim())
  );
  const canMovePastIncomplete = hasCustomerInfo && hasVehicleInfo;
  const lockReason = !hasCustomerInfo
    ? 'Add customer name, email, and phone.'
    : !hasVehicleInfo
    ? 'Add vehicle details (license plate, make, or model).'
    : null;

  return {
    hasCustomerInfo,
    hasVehicleInfo,
    canMovePastIncomplete,
    lockReason,
  };
};

export const validateStatusTransition = (previous, nextStatus, userRole) => {
  if (!previous) {
    return { valid: true };
  }

  const isManager = hasRole(userRole, RoleTypes.SiteManager);

  if (!getValidClaimStatusTransitions(previous.status).includes(Number(nextStatus))) {
    return {
      valid: false,
      error: 'Invalid status change',
      message: 'This claim cannot move to that status.',
    };
  }

  // Check: Incomplete can only move to Open
  if (
    Number(previous.status) === ReclamationStatus.Incomplete &&
    Number(nextStatus) !== ReclamationStatus.Open
  ) {
    return {
      valid: false,
      error: 'Status locked',
      message: 'Incomplete claims can only move to Open once they meet requirements.',
    };
  }

  // Check: Incomplete to Open requires completion
  if (
    Number(previous.status) === ReclamationStatus.Incomplete &&
    Number(nextStatus) === ReclamationStatus.Open
  ) {
    const completion = getCompletionInfo(previous);
    if (!completion.canMovePastIncomplete) {
      return {
        valid: false,
        error: 'Missing info',
        message: completion.lockReason || 'Add required details before moving the claim to Open.',
      };
    }
  }

  // Estimates Required, Approved and Denied unlock 24 hours after the claim was created.
  if (
    CLAIM_DECISION_STATUSES.includes(Number(nextStatus)) &&
    isClaimDecisionLocked(previous.createdAt)
  ) {
    return {
      valid: false,
      error: 'Status locked',
      message:
        'Estimates Required, Approved and Denied unlock 24 hours after the claim was created.',
    };
  }

  // Check: Manager permission required for Investigating transitions
  if (
    Number(previous.status) === ReclamationStatus.Investigating &&
    Number(nextStatus) !== ReclamationStatus.Investigating
  ) {
    if (!isManager) {
      return {
        valid: false,
        error: 'Permission required',
        message: 'Only managers and admins can change claim status.',
      };
    }
  }

  return { valid: true };
};

// With a designated repair shop, Estimates Required sends the designated repair shop template
// instead of asking the guest for estimates from shops of their choice.
export const getStatusEmailTemplate = (previous, nextStatus, estimates) => {
  const templateKey = shouldSendStatusEmail(previous, nextStatus);
  if (
    templateKey &&
    Number(nextStatus) === ReclamationStatus.RepairApproved &&
    estimates?.designatedRepairShop
  ) {
    return 'EmailClaimDesignatedRepairShop';
  }
  return templateKey;
};

export const shouldSendStatusEmail = (previous, nextStatus) => {
  if (!previous) return null;

  // Incomplete -> Open: Send claim message
  if (
    Number(previous.status) === ReclamationStatus.Incomplete &&
    Number(nextStatus) === ReclamationStatus.Open
  ) {
    return 'EmailCustomerClaimMessage';
  }

  // Any -> Investigating: Send investigating message
  if (
    Number(previous.status) !== ReclamationStatus.Investigating &&
    Number(nextStatus) === ReclamationStatus.Investigating
  ) {
    return 'EmailClaimInvestigating';
  }

  // Resolved is the final status, so it sends the Completed template.
  if (
    Number(previous.status) !== ReclamationStatus.Resolved &&
    Number(nextStatus) === ReclamationStatus.Resolved
  ) {
    return 'EmailClaimCompleted';
  }

  // Any -> Repair Approved: Send repair approval estimates required message
  if (
    Number(previous.status) !== ReclamationStatus.RepairApproved &&
    Number(nextStatus) === ReclamationStatus.RepairApproved
  ) {
    return 'EmailClaimRepairApproval';
  }

  if (
    Number(previous.status) !== ReclamationStatus.FinalApproval &&
    Number(nextStatus) === ReclamationStatus.FinalApproval
  ) {
    return 'EmailClaimFinalApproval';
  }

  if (
    Number(previous.status) !== ReclamationStatus.RepairDenied &&
    Number(nextStatus) === ReclamationStatus.RepairDenied
  ) {
    return 'EmailClaimDenied';
  }

  if (
    Number(previous.status) !== ReclamationStatus.Completed &&
    Number(nextStatus) === ReclamationStatus.Completed
  ) {
    return 'EmailClaimCompleted';
  }

  return null;
};
